// Página UserForm - Formulario para crear/editar usuarios
// Página completa para gestionar usuarios del sistema

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createUser, getUser, updateUser } from '../../services/users.service'
import { getAllRoles } from '../../services/roles.service'
import { FIXED_ROLES } from '../../constants/permissions'
import { normalizeWhatsAppPhoneAR } from '../../utils/whatsapp'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './UserForm.css'

/**
 * Página UserForm
 * Formulario completo para crear o editar usuarios
 */
function UserForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  const [customRoles, setCustomRoles] = useState([])

  // Estado del formulario
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    role: FIXED_ROLES.CLIENT,
    roleId: null, // ID del rol personalizado (si aplica)
    phone: '',
    phoneAlternate: '',
    addresses: [],
    primaryAddressIndex: null,
    notes: ''
  })

  // Estado para nueva dirección
  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    postalCode: '',
    province: ''
  })

  const fetchCustomRoles = useCallback(async () => {
    try {
      const roles = await getAllRoles()
      setCustomRoles(roles.filter(r => r.active))
    } catch (err) {
      console.error('Error cargando roles personalizados:', err)
    }
  }, [])

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true)
      const user = await getUser(id)
      
      if (user) {
        setFormData({
          email: user.email || '',
          password: '', // No cargar contraseña
          displayName: user.displayName || '',
          role: user.role || FIXED_ROLES.CLIENT,
          roleId: user.roleId || null,
          phone: user.phone || '',
          phoneAlternate: user.phoneAlternate || '',
          addresses: user.addresses || [],
          primaryAddressIndex: user.primaryAddressIndex !== undefined ? user.primaryAddressIndex : null,
          notes: user.notes || ''
        })
      }
    } catch (error) {
      console.error('Error cargando usuario:', error)
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos del usuario'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchCustomRoles()
    if (isEditing) {
      fetchUserData()
    }
  }, [isEditing, fetchCustomRoles, fetchUserData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (saving) return

    // Validación básica
    if (!formData.email) {
      setNotification({
        type: 'error',
        message: 'El email es requerido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!isEditing && !formData.password) {
      setNotification({
        type: 'error',
        message: 'La contraseña es requerida para nuevos usuarios'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!isEditing && formData.password.length < 6) {
      setNotification({
        type: 'error',
        message: 'La contraseña debe tener al menos 6 caracteres'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const phoneRaw = typeof formData.phone === 'string' ? formData.phone.trim() : ''
    const phoneAltRaw = typeof formData.phoneAlternate === 'string' ? formData.phoneAlternate.trim() : ''

    const normalizedPhone = phoneRaw ? normalizeWhatsAppPhoneAR(phoneRaw) : { normalized: '', isValid: true }
    if (phoneRaw && !normalizedPhone.isValid) {
      setNotification({
        type: 'error',
        message: 'El teléfono principal debe estar en formato WhatsApp 549XXXXXXXXXX'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const normalizedAlt = phoneAltRaw ? normalizeWhatsAppPhoneAR(phoneAltRaw) : { normalized: '', isValid: true }
    if (phoneAltRaw && !normalizedAlt.isValid) {
      setNotification({
        type: 'error',
        message: 'El teléfono alternativo debe estar en formato WhatsApp 549XXXXXXXXXX'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)

    try {
      const payloadPhones = {
        phone: phoneRaw ? normalizedPhone.normalized : '',
        phoneAlternate: phoneAltRaw ? normalizedAlt.normalized : ''
      }
      if (isEditing) {
        // Actualizar usuario existente
        await updateUser(id, {
          displayName: formData.displayName,
          role: formData.role,
          roleId: formData.roleId,
          ...payloadPhones,
          addresses: formData.addresses,
          primaryAddressIndex: formData.primaryAddressIndex,
          notes: formData.notes
        })
        
        setNotification({
          type: 'success',
          message: 'Usuario actualizado correctamente'
        })
      } else {
        // Crear nuevo usuario
        await createUser({ ...formData, ...payloadPhones })
        
        setNotification({
          type: 'success',
          message: `Usuario ${formData.email} creado correctamente`
        })
      }
      
      setTimeout(() => {
        navigate('/admin/usuarios')
      }, 1500)
    } catch (error) {
      console.error('Error guardando usuario:', error)
      setNotification({
        type: 'error',
        message: `Error al guardar usuario: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // Si es el campo de rol, verificar si es un rol personalizado
    if (name === 'role') {
      // Si el valor es un ID de rol personalizado
      if (customRoles.some(r => r.id === value)) {
        const customRole = customRoles.find(r => r.id === value)
        setFormData(prev => ({
          ...prev,
          role: customRole.name,
          roleId: value
        }))
      } else {
        // Es un rol fijo
        setFormData(prev => ({
          ...prev,
          role: value,
          roleId: null
        }))
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const addAddress = () => {
    if (newAddress.street && newAddress.city && newAddress.province) {
      const newAddresses = [...formData.addresses, { ...newAddress }]
      setFormData(prev => ({
        ...prev,
        addresses: newAddresses,
        primaryAddressIndex: prev.primaryAddressIndex === null ? 0 : prev.primaryAddressIndex
      }))
      setNewAddress({
        street: '',
        city: '',
        postalCode: '',
        province: ''
      })
    }
  }

  const removeAddress = (index) => {
    const newAddresses = formData.addresses.filter((_, i) => i !== index)
    let newPrimaryIndex = formData.primaryAddressIndex
    
    if (formData.primaryAddressIndex === index) {
      newPrimaryIndex = newAddresses.length > 0 ? 0 : null
    } else if (formData.primaryAddressIndex > index) {
      newPrimaryIndex = formData.primaryAddressIndex - 1
    }
    
    setFormData(prev => ({
      ...prev,
      addresses: newAddresses,
      primaryAddressIndex: newPrimaryIndex
    }))
  }

  const setPrimaryAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      primaryAddressIndex: index
    }))
  }

  if (loading) {
    return (
      <div className="user-form-page">
        <div className="loading-state">
          <p>Cargando datos del usuario...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="user-form-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="form-header">
        <div>
          <button
            className="back-button"
            onClick={() => navigate('/admin/usuarios')}
          >
            ← Volver
          </button>
          <h1>{isEditing ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</h1>
          <p className="form-subtitle">
            {isEditing 
              ? 'Modifica la información del usuario del sistema'
              : 'Completa los datos para crear un nuevo usuario del sistema'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="user-form">
        {/* Información básica */}
        <div className="form-section">
          <h2 className="section-title">Información Básica</h2>
          
          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="usuario@example.com"
              required
              disabled={isEditing || saving}
              className="form-input"
            />
            {isEditing && (
              <small className="form-hint">El email no se puede modificar</small>
            )}
          </div>

          {!isEditing && (
            <div className="form-group">
              <label htmlFor="password">
                Contraseña Temporal <span className="required">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mínimo 6 caracteres"
                minLength={6}
                required
                disabled={saving}
                className="form-input"
              />
              <small className="form-hint">
                El usuario deberá cambiar esta contraseña al iniciar sesión
              </small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="displayName">Nombre Completo</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              placeholder="Nombre completo"
              disabled={saving}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="role">
              Rol <span className="required">*</span>
            </label>
            <select
              id="role"
              name="role"
              value={formData.roleId || formData.role || FIXED_ROLES.CLIENT}
              onChange={handleChange}
              required
              disabled={saving}
              className="form-input"
            >
              <option value={FIXED_ROLES.CLIENT}>Cliente</option>
              <option value={FIXED_ROLES.ADMIN}>Administrador</option>
              <option value={FIXED_ROLES.SUPERADMIN}>Super Admin</option>
              {customRoles.length > 0 && (
                <optgroup label="Roles Personalizados">
                  {customRoles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <small className="form-hint">
              {formData.roleId 
                ? `Rol personalizado: ${customRoles.find(r => r.id === formData.roleId)?.description || ''}`
                : 'Selecciona un rol fijo o personalizado para el usuario'}
            </small>
          </div>
        </div>

        {/* Información de contacto */}
        <div className="form-section">
          <h2 className="section-title">Información de Contacto</h2>
          
          <div className="form-group">
            <label htmlFor="phone">Teléfono Principal</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="5493813333333"
              disabled={saving}
              className="form-input"
            />
            <small className="form-hint">Formato WhatsApp requerido: 549XXXXXXXXXX</small>
          </div>

          <div className="form-group">
            <label htmlFor="phoneAlternate">Teléfono Alternativo</label>
            <input
              type="tel"
              id="phoneAlternate"
              name="phoneAlternate"
              value={formData.phoneAlternate}
              onChange={handleChange}
              placeholder="5493813333333"
              disabled={saving}
              className="form-input"
            />
            <small className="form-hint">Formato WhatsApp requerido: 549XXXXXXXXXX</small>
          </div>
        </div>

        {/* Direcciones */}
        <div className="form-section">
          <h2 className="section-title">Direcciones</h2>
          
          {formData.addresses.length > 0 && (
            <div className="addresses-list">
              {formData.addresses.map((addr, index) => (
                <div key={index} className="address-item">
                  <div className="address-content">
                    <span className="address-text">
                      {addr.street}, {addr.city}, {addr.province} {addr.postalCode && `(${addr.postalCode})`}
                    </span>
                    {formData.primaryAddressIndex === index && (
                      <span className="primary-badge">Principal</span>
                    )}
                  </div>
                  <div className="address-actions">
                    {formData.primaryAddressIndex !== index && (
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => setPrimaryAddress(index)}
                        disabled={saving}
                      >
                        Marcar como principal
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-link btn-danger"
                      onClick={() => removeAddress(index)}
                      disabled={saving}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="new-address-form">
            <h3 className="subsection-title">Agregar Nueva Dirección</h3>
            <div className="form-row">
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Calle y número"
                  value={newAddress.street}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
                  disabled={saving}
                  className="form-input"
                />
              </div>
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Ciudad"
                  value={newAddress.city}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                  disabled={saving}
                  className="form-input"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Código Postal"
                  value={newAddress.postalCode}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                  disabled={saving}
                  className="form-input"
                />
              </div>
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Provincia"
                  value={newAddress.province}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, province: e.target.value }))}
                  disabled={saving}
                  className="form-input"
                />
              </div>
            </div>
            <button
              type="button"
              className="btn-add-address"
              onClick={addAddress}
              disabled={saving}
            >
              ➕ Agregar Dirección
            </button>
          </div>
        </div>

        {/* Notas */}
        <div className="form-section">
          <h2 className="section-title">Notas / Observaciones</h2>
          <div className="form-group">
            <label htmlFor="notes">Notas</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Notas adicionales sobre el usuario..."
              rows={4}
              disabled={saving}
              className="form-input"
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/usuarios')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : (isEditing ? 'Actualizar Usuario' : 'Crear Usuario')}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default UserForm

